import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { KEY_ENTER_COMMAND, COMMAND_PRIORITY_CRITICAL } from 'lexical';

/** ⌘/Ctrl+Enter sends instead of inserting a newline — the composer's
 *  documented shortcut (ruling 80), carried over from the plain Textarea's
 *  own onKeyDown handler now that the editor owns Enter's default behaviour.
 *  Registered at CRITICAL priority so it runs before RichTextPlugin's own
 *  Enter handler (which inserts a paragraph break at EDITOR priority) —
 *  otherwise that handler claims the event first and this one never runs. */
export function SubmitShortcutPlugin({ onSubmit }: { onSubmit: () => void }) {
  const [editor] = useLexicalComposerContext();
  // ThreadComposer's `send` closure is a fresh function every render (it
  // closes over body/bodyHtml/etc, which change on every keystroke) — a ref
  // keeps the command registered once instead of tearing down and
  // re-registering on every edit.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  });

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (!event || !(event.metaKey || event.ctrlKey)) return false;
        event.preventDefault();
        onSubmitRef.current();
        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);
  return null;
}
